import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Spinner,
  AppShell,
  matchActivePath,
  navItemPaths,
  itemOwnsPath,
  filterSidebarItems,
} from '@bbpos/ui';
import type { SidebarItem } from '@bbpos/ui';
import type { PlanFeatureKey } from '@bbpos/types';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { HomePage } from '@/pages/HomePage';
import { TenantListPage } from '@/pages/TenantListPage';
import { TenantCreatePage } from '@/pages/TenantCreatePage';
import { TenantDetailPage } from '@/pages/TenantDetailPage';
import { UserListPage } from '@/pages/UserListPage';
import { UserCreatePage } from '@/pages/UserCreatePage';
import { UserDetailPage } from '@/pages/UserDetailPage';
import { LocationListPage } from '@/pages/LocationListPage';
import { LocationCreatePage } from '@/pages/LocationCreatePage';
import { LocationDetailPage } from '@/pages/LocationDetailPage';
import { ProductListPage } from '@/pages/ProductListPage';
import { ProductCreatePage } from '@/pages/ProductCreatePage';
import { ProductImportPage } from '@/pages/ProductImportPage';
import { ProductDetailPage } from '@/pages/ProductDetailPage';
import { ProductVariantEditPage } from '@/pages/ProductVariantEditPage';
import { VariantAttributesPage } from '@/pages/VariantAttributesPage';
import { MasterCataloguePage } from '@/pages/product-masters/MasterCataloguePage';
import { MasterConflictsPage } from '@/pages/product-masters/MasterConflictsPage';
import { InventoryDashboardPage } from '@/pages/InventoryDashboardPage';
import { InventoryValuationPage } from '@/pages/InventoryValuationPage';
import { LowStockAlertsPage } from '@/pages/LowStockAlertsPage';
import { ExpiryAlertsPage } from '@/pages/ExpiryAlertsPage';
import { CategoryListPage } from '@/pages/CategoryListPage';
import { CategoryDetailPage } from '@/pages/CategoryDetailPage';
import { ExpenseListPage } from '@/pages/ExpenseListPage';
import { OnlineOrderListPage } from '@/pages/OnlineOrderListPage';
import { SiteContentPage } from '@/pages/SiteContentPage';
import { OnlineOrderDetailPage } from '@/pages/OnlineOrderDetailPage';
import { ExpenseCreatePage } from '@/pages/ExpenseCreatePage';
import { ExpenseDetailPage } from '@/pages/ExpenseDetailPage';
import { ApprovalsPage } from '@/pages/ApprovalsPage';
import {
  ReportsDashboardPage,
  SalesReportPage,
  GstReportPage,
  Gstr1ExportPage,
  ProfitLossReportPage,
  PaymentBreakdownPage,
  RegisterSessionsReportPage,
  InventoryReportPage,
  TopProductsReportPage,
  CashierPerformancePage,
  PeakHoursReportPage,
  DiscountAnalysisPage,
  CategorySalesReportPage,
  TopCustomersReportPage,
  RepeatVisitsReportPage,
  TableTurnoverReportPage,
  KitchenPerformanceReportPage,
  CancellationTrendsReportPage,
  ExpenseAnalysisReportPage,
  StaffExpenseReportPage,
  TallyExportPage,
  PurchaseRegisterReportPage,
  PayablesAgeingReportPage,
  InputTaxReportPage,
  DemandForecastPage,
} from '@/pages/reports';
import { ReconciliationDashboardPage } from '@/pages/payments';
import { ReportGuard, getReport, canAccessReport } from '@/features/reports';
import {
  CustomerListPage,
  CustomerCreatePage,
  RfmDashboardPage,
  CustomerFeedbackPage,
  CustomerDetailPage,
} from '@/pages/customers';
import { LoyaltyDashboardPage } from '@/pages/loyalty';
import { VoucherListPage, VoucherCreatePage, VoucherDetailPage } from '@/pages/vouchers';
import {
  MembershipPlansListPage,
  MembershipPlanCreatePage,
  MembershipPlanDetailPage,
  MembershipPlanEditPage,
} from '@/pages/memberships';
import {
  CommunicationHubPage,
  TemplatesPage,
  CampaignsPage,
  DealOfDayPage,
  CommunicationLogsPage,
  InvoicePreviewPage,
  SocialMediaPostCreatorPage,
  FeedbackViewerPage,
  ReorderLinksPage,
} from '@/pages/communications';
import { CommissionRatesPage, CommissionSettlementsPage } from '@/pages/commissions';
import { ShiftManagementPage, AttendancePage, ShiftSwapsPage, WorkingSchedulesPage } from '@/pages/shifts';
import { CreditAccountsListPage, CreditAccountDetailPage, CreditInvoicesListPage } from '@/pages/credits';
import { CorporateAccountsListPage, CorporateAccountDetailPage } from '@/pages/corporate';
import { TableManagementPage } from '@/pages/tables/TableManagementPage';
import { FloorPlanEditorPage } from '@/pages/tables/FloorPlanEditorPage';
import { ReservationsPage } from '@/pages/reservations/ReservationsPage';
import { RecipeListPage } from '@/pages/recipes/RecipeListPage';
import { RecipeDetailPage } from '@/pages/recipes/RecipeDetailPage';
import { IngredientListPage } from '@/pages/ingredients/IngredientListPage';
// G3.15 — Kitchen staff-to-station assignment
import { StaffAssignmentPage } from '@/pages/kitchen/StaffAssignmentPage';
import { MenuManagementPage } from '@/pages/menus/MenuManagementPage';
import { MenuPeriodsPage } from '@/pages/menus/MenuPeriodsPage';
import { ModifierManagementPage } from '@/pages/modifiers/ModifierManagementPage';
import { ComboListPage } from '@/pages/combos/ComboListPage';
import { ComboCreatePage } from '@/pages/combos/ComboCreatePage';
import { ComboDetailPage } from '@/pages/combos/ComboDetailPage';
import { WastageTrackingPage } from '@/pages/wastage/WastageTrackingPage';
import { FestiveEventsListPage } from '@/pages/festive/FestiveEventsListPage';
import { FestiveEventDetailPage } from '@/pages/festive/FestiveEventDetailPage';
import { FestiveOrdersPage } from '@/pages/festive/FestiveOrdersPage';
import { FestiveMenuEditorPage } from '@/pages/festive/FestiveMenuEditorPage';
import { FestiveAlertsPage } from '@/pages/festive/FestiveAlertsPage';
import { FestiveCapacityPage } from '@/pages/festive/FestiveCapacityPage';
import { ProductionPlanningPage } from '@/pages/festive/ProductionPlanningPage';
import {
  BookingsListPage,
  BookingsCalendarPage,
  BookingsDetailPage,
} from '@/pages/bookings';
import {
  BusinessSettingsPage, TaxConfigPage, CashRoundingPage, NotificationPrefsPage, BrandingPage, PaymentGatewayPage,
  SmsConfigPage, EmailConfigPage, WhatsAppConfigPage, SidebarPreferencesPage, ReturnPolicyPage, InventoryTrackingPage, PricingSettingsPage,
  WastageLimitsPage,
} from '@/pages/settings';
import { CashClosingHistoryPage } from '@/pages/cash-closing';
import { MyAccountPage } from '@/pages/account';
import { AuditLogsPage } from '@/pages/audit';
import { BackupManagementPage } from '@/pages/backups';
import { DataExportPage } from '@/pages/data-export';
import { BillDetailPage, BillListPage } from '@/pages/bills';
import { DeadLettersPage } from '@/pages/dead-letters';
// G2.24: Admin Sync Health — cross-tenant conflict resolution
import { AdminSyncHealthPage } from '@/pages/AdminSyncHealthPage';
// T09: offline bills taxed differently from the product master
import { TaxMismatchesPage } from '@/pages/TaxMismatchesPage';
import { OfflinePaymentsPage } from '@/pages/OfflinePaymentsPage';
import { SystemHealthPage } from '@/pages/SystemHealthPage';
import { QuotationListPage, QuotationDetailPage, QuotationDashboardPage } from '@/pages/quotations';
import { SalesOrderListPage, SalesOrderDetailPage, SalesOrderDeliverPage, SalesOrderInvoicePage } from '@/pages/sales-orders';
import { SalesInvoiceListPage, SalesInvoiceDetailPage } from '@/pages/sales-invoices';
import {
  PurchaseOrderListPage,
  PurchaseOrderHubPage,
  DailyPoTemplatePage,
  PurchaseOrderCreatePage,
  PurchaseOrderDetailPage,
  PurchaseOrderReceivePage,
  PurchaseOrderAmendPage,
} from '@/pages/purchase-orders';
import { RfqListPage, RfqCreatePage, RfqDetailPage } from '@/pages/rfqs';
import { VendorBillListPage, VendorBillCreatePage, VendorBillDetailPage } from '@/pages/vendor-bills';
import {
  StockTransferListPage,
  StockTransferCreatePage,
  StockTransferDetailPage,
} from '@/pages/stock-transfers';
import {
  SupplierListPage,
  SupplierCreatePage,
  SupplierDetailPage,
} from '@/pages/suppliers';
import {
  PlatformUserListPage,
  PlatformUserCreatePage,
  PlatformUserDetailPage,
} from '@/pages/platform-users';
import { PlatformEmailPage } from '@/pages/platform-email';
import { CompanyProfilePage } from '@/pages/platform-company-profile';
import { EnquiriesPage } from '@/pages/platform-enquiries';
import { useNewEnquiryCount } from '@/hooks/use-new-enquiry-count';
import { useUnreadNotificationCount } from '@/hooks/use-unread-notification-count';
import { ExchangeRatesPage } from '@/pages/exchange-rates';
import { StaffPerformancePage } from '@/pages/staff';
import { DiscountRulesPage } from '@/pages/discounts';
import { BroadcastCenterPage, NotificationCenterPage, NotificationDetailPage } from '@/pages/notifications';
import { MfaEnforcementPage } from '@/pages/security';
import {
  SubscriptionListPage,
  SubscriptionPlansPage,
  MySubscriptionPage,
} from '@/pages/subscriptions';
import { useAuthStore } from '@/stores/use-auth-store';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { headerAllLocations, headerLocationSelection } from '@/lib/header-locations';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { usePlanFeature, usePlanFeatures, type HasPlanFeature } from '@/hooks/use-plan-feature';
import { LockedFeature } from '@/components/features/subscriptions/LockedFeature';
import { UsageBanner } from '@/components/UsageBanner';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { guardedLeave } from '@/hooks/use-unsaved-changes-guard';
import {
  useSidebarPrefsStore,
  NON_HIDEABLE_SIDEBAR_IDS,
  resolveStartPath,
} from '@/stores/use-sidebar-prefs-store';
import { ModuleIcon } from '@/constants/module-icons';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isHydrated } = useAuthStore();

  if (!isHydrated || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner variant="page" size="md" label="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnUrl = window.location.pathname + window.location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(returnUrl)}`} replace />;
  }

  return <>{children}</>;
}

function RoleProtectedRoute({
  children,
  minRoleLevel,
}: {
  children: React.ReactNode;
  minRoleLevel: number;
}) {
  const { profile, isHydrated, isLoading } = useAuthStore();

  // Wait for auth hydration before role check — profile is null while getMe() is in-flight.
  // Without this guard, a direct URL navigation to a role-gated route silently redirects to
  // /dashboard because profile=null yields userRoleLevel=0, which is always < any minRoleLevel.
  if (!isHydrated || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner variant="page" size="md" label="Loading..." />
      </div>
    );
  }

  const userRoleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;

  if (userRoleLevel < minRoleLevel) {
    return <Navigate to={getStartPath()} replace />;
  }

  return <ProtectedRoute>{children}</ProtectedRoute>;
}

// Plan-limits plan §7 — lock, never hide. A route whose feature is not in the
// business's plan still opens, but shows LockedFeature instead of the page. It sits
// inside the role check and the layout, so a role refusal still wins and the
// sidebar stays on screen.
export function PlanFeatureRoute({
  children,
  feature,
}: {
  children: React.ReactNode;
  feature: PlanFeatureKey;
}) {
  const allowed = usePlanFeature(feature);
  return allowed ? <>{children}</> : <LockedFeature feature={feature} />;
}

function RootRedirect() {
  const { isAuthenticated, isHydrated } = useAuthStore();

  if (!isHydrated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner variant="page" size="md" label="Loading..." />
      </div>
    );
  }

  return isAuthenticated ? (
    <Navigate to={getStartPath()} replace />
  ) : (
    <Navigate to="/login" replace />
  );
}

// Read the persisted startPage preference outside of React. Used by
// redirect components (RootRedirect, RoleProtectedRoute fallback) where
// hooks would be awkward or trigger unnecessary re-renders.
// L06: role-aware — a cashier who once saved "dashboard" lands on /home, so the
// MANAGER-only /dashboard route can never redirect to itself.
function getStartPath(): string {
  const page = useSidebarPrefsStore.getState().startPage;
  const roleLevel = ROLE_LEVELS[useAuthStore.getState().profile?.role ?? ''] ?? 0;
  return resolveStartPath(page, roleLevel);
}

// ─── Sidebar nav items ──────────────────────────────────────────────────────

const NAV_ICON_CLASS = 'h-5 w-5';

function Icon({ d }: { d: string }) {
  return (
    <svg className={NAV_ICON_CLASS} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

// Local extension of SidebarItem to mark items that should be hidden from
// platform roles (SUPER_ADMIN / ADMIN / MODERATOR). These platform users
// access per-tenant data only by drilling into a specific tenant at
// /tenants/:id — exposing these items in the top-level sidebar would both
// clutter the UX and invite cross-tenant data leaks.
//
// `planFeature` names the plan feature an item needs (docs/plan-limits-plan.md §3).
// Missing from the plan, the item is shown locked, never hidden — see applyPlanLocks.
export type AdminSidebarItem = Omit<SidebarItem, 'children'> & {
  hideForPlatform?: boolean;
  featureFlag?: string;
  planFeature?: PlanFeatureKey;
  children?: AdminSidebarItem[];
};

export const SIDEBAR_ITEMS: AdminSidebarItem[] = [
  { id: 'home', icon: <ModuleIcon id="home" />, label: 'Home', path: '/home' },
  // L06: the dashboard opens for MANAGER and up, so a cashier is never shown a link to it.
  { id: 'dashboard', icon: <ModuleIcon id="dashboard" />, label: 'Dashboard', path: '/dashboard', requiredRole: 30 },
  { id: 'tenants', group: 'Platform', icon: <ModuleIcon id="tenants" />, label: 'Tenants', path: '/tenants', requiredRole: 60 },
  // G3.5: Platform User Management — Super Admin / Admin / Moderator accounts.
  // Visible ONLY to platform roles; tenant users (OWNER/MANAGER/CASHIER) never see this.
  { id: 'platform-users', group: 'Platform', icon: <ModuleIcon id="platform-users" />, label: 'Platform Users', path: '/platform-users', requiredRole: 60 },
  // G3.8: Subscription & Plan Management — platform-facing list + plan catalog.
  // MODERATOR+ (60) can view; ADMIN+ (80) can upgrade/downgrade/renew; SUPER_ADMIN only can CRUD plans.
  {
    id: 'billing-platform',
    group: 'Platform',
    icon: <ModuleIcon id="billing-platform" />,
    label: 'Billing',
    path: '/subscriptions',
    requiredRole: 60,
    children: [
      { id: 'subscriptions-list', icon: <Icon d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />, label: 'Subscriptions', path: '/subscriptions', requiredRole: 60 },
      { id: 'subscription-plans', icon: <Icon d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />, label: 'Plans', path: '/subscription-plans', requiredRole: 60 },
    ],
  },
  // QA-ADM-455: System Health — platform-only (ADMIN+), no tenant data exposure.
  { id: 'system-health', group: 'Platform', icon: <ModuleIcon id="system-health" />, label: 'System Health', path: '/system-health', requiredRole: 60 },
  // Email SMTP plan §7/§9: the platform's own outbound mailbox. ADMIN+ can look
  // (support needs to see whether OTP mail is going out); only SUPER_ADMIN writes.
  { id: 'platform-email', group: 'Platform', icon: <ModuleIcon id="platform-email" />, label: 'Platform Email', path: '/platform/email', requiredRole: 80 },
  // Website plan §6.2: the company facts orio360.com shows. ADMIN+ can look;
  // only SUPER_ADMIN writes.
  { id: 'platform-company-profile', group: 'Platform', icon: <ModuleIcon id="platform-company-profile" />, label: 'Company Profile', path: '/platform/company-profile', requiredRole: 80 },
  // Website plan §6.4: what people send from the website form. MODERATOR+ — the
  // same people who can create a business. The badge is the New count.
  { id: 'platform-enquiries', group: 'Platform', icon: <ModuleIcon id="platform-enquiries" />, label: 'Enquiries', path: '/platform/enquiries', requiredRole: 60 },
  { id: 'dead-letters', group: 'Platform', icon: <ModuleIcon id="dead-letters" />, label: 'Dead Letters', path: '/dead-letters', requiredRole: 80 },
  // Sales Orders sit before Quotations: a quote becomes an order once the customer's PO arrives.
  // Sales, not Finance. An online order is a request a customer made, worked
  // before any money moves; it becomes a Bill only once somebody accepts it,
  // and Finance is where the resulting document lives.
  { id: 'website-pages', group: 'Sales', icon: <ModuleIcon id="website-pages" />, label: 'Website Pages', path: '/website/pages', requiredRole: 30, hideForPlatform: true, planFeature: 'online_store' },
  { id: 'online-orders', group: 'Sales', icon: <ModuleIcon id="online-orders" />, label: 'Online Orders', path: '/online-orders', requiredRole: 5, hideForPlatform: true, planFeature: 'online_store' },
  { id: 'sales-orders', group: 'Sales', icon: <ModuleIcon id="sales-orders" />, label: 'Sales Orders', path: '/sales-orders', requiredRole: 5, hideForPlatform: true, planFeature: 'quotations' },
  {
    id: 'quotations',
    group: 'Sales',
    icon: <ModuleIcon id="quotations" />,
    label: 'Quotations',
    path: '/quotations',
    requiredRole: 5,
    hideForPlatform: true,
    planFeature: 'quotations',
    children: [
      { id: 'quotations-list', icon: <Icon d="M3 10h18M3 14h18M3 18h18M3 6h18" />, label: 'All Quotes', path: '/quotations', planFeature: 'quotations' },
      { id: 'quotations-dashboard', icon: <Icon d="M3 3v18h18M7 14l4-4 3 3 5-5" />, label: 'Dashboard', path: '/quotations/analytics', requiredRole: 30, planFeature: 'quotations' },
    ],
  },
  { id: 'discounts', group: 'Sales', icon: <ModuleIcon id="discounts" />, label: 'Discounts', path: '/discounts', requiredRole: 50, hideForPlatform: true },
  {
    id: 'credit-accounts',
    group: 'Sales',
    icon: <ModuleIcon id="credit-accounts" />,
    label: 'Credit Accounts',
    path: '/credit-accounts',
    requiredRole: 50,
    hideForPlatform: true,
    featureFlag: 'ENABLE_CREDIT_BILLING',
    planFeature: 'credit_billing',
    children: [
      { id: 'credit-accounts-list', icon: <Icon d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />, label: 'Accounts', path: '/credit-accounts', requiredRole: 50, planFeature: 'credit_billing' },
      { id: 'credit-invoices', icon: <Icon d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />, label: 'Invoices', path: '/credit-invoices', requiredRole: 50, planFeature: 'credit_billing' },
    ],
  },
  { id: 'products', group: 'Catalog', icon: <ModuleIcon id="products" />, label: 'Products', path: '/products', requiredRole: 30, hideForPlatform: true },
  // One list of products for every store, and which stores sell each (master catalogue plan §11).
  { id: 'product-masters', group: 'Catalog', icon: <ModuleIcon id="product-masters" />, label: 'Master Catalogue', path: '/products/masters', requiredRole: 30, hideForPlatform: true },
  { id: 'categories', group: 'Catalog', icon: <ModuleIcon id="categories" />, label: 'Categories', path: '/categories', requiredRole: 30, hideForPlatform: true },
  { id: 'variant-attributes', group: 'Catalog', icon: <ModuleIcon id="variant-attributes" />, label: 'Variant Attributes', path: '/products/variant-attributes', requiredRole: 30, hideForPlatform: true },
  // Inventory and Purchase are sibling sections, each a flat list of pages —
  // no wrapper module row, the section header is the module.
  { id: 'inventory-dashboard', group: 'Inventory', icon: <ModuleIcon id="inventory-dashboard" />, label: 'Overview', path: '/inventory', requiredRole: 30, hideForPlatform: true },
  { id: 'inventory-valuation', group: 'Inventory', icon: <ModuleIcon id="inventory-valuation" />, label: 'Valuation', path: '/inventory/valuation', requiredRole: 30, hideForPlatform: true },
  // Ops simulation T13: on hand by batch/serial, incoming/outgoing, forecast, min/max.
  { id: 'inventory-report', group: 'Inventory', icon: <ModuleIcon id="inventory-report" />, label: 'Inventory Report', path: '/inventory/report', requiredRole: 30, hideForPlatform: true },
  { id: 'inventory-low-stock', group: 'Inventory', icon: <ModuleIcon id="inventory-low-stock" />, label: 'Low Stock', path: '/inventory/low-stock', requiredRole: 30, hideForPlatform: true },
  { id: 'inventory-expiry', group: 'Inventory', icon: <ModuleIcon id="inventory-expiry" />, label: 'Expiry Alerts', path: '/inventory/expiry-alerts', requiredRole: 30, hideForPlatform: true, planFeature: 'batch_serial' },
  { id: 'inventory-transfers', group: 'Inventory', icon: <ModuleIcon id="inventory-transfers" />, label: 'Stock Transfers', path: '/inventory/stock-transfers', requiredRole: 30, hideForPlatform: true, planFeature: 'stock_transfers' },
  { id: 'inventory-wastage', group: 'Inventory', icon: <ModuleIcon id="inventory-wastage" />, label: 'Wastage', path: '/inventory/wastage', requiredRole: 5, hideForPlatform: true },
  // Procurement pipeline, in the order it is worked: pick the supplier, ask them
  // to quote (RFQ), then raise the PO against the quote.
  { id: 'inventory-suppliers', group: 'Purchase', icon: <ModuleIcon id="inventory-suppliers" />, label: 'Suppliers', path: '/inventory/suppliers', requiredRole: 30, hideForPlatform: true, planFeature: 'purchase' },
  { id: 'rfq', group: 'Purchase', icon: <ModuleIcon id="rfq" />, label: 'RFQ', path: '/inventory/rfqs', requiredRole: 30, hideForPlatform: true, planFeature: 'purchase' },
  // The item opens the hub (/purchase-orders); the PO list, detail, create and
  // receive pages live under /inventory/purchase-orders, so they highlight it too.
  { id: 'purchase-order', group: 'Purchase', icon: <ModuleIcon id="purchase-order" />, label: 'Purchase Order', path: '/purchase-orders', matchPaths: ['/inventory/purchase-orders'], requiredRole: 30, hideForPlatform: true, planFeature: 'purchase' },
  {
    id: 'customers',
    group: 'Customers',
    icon: <ModuleIcon id="customers" />,
    label: 'Customers',
    path: '/customers',
    requiredRole: 30,
    hideForPlatform: true,
    children: [
      { id: 'customers-list',     icon: <Icon d="M3 10h18M3 14h18M3 18h18M3 6h18" />, label: 'All Customers', path: '/customers' },
      { id: 'customers-new',      icon: <Icon d="M12 4v16m8-8H4" />, label: 'Add Customer', path: '/customers/new' },
      { id: 'customers-rfm',      icon: <Icon d="M3 3v18h18M7 14l4-4 3 3 5-5" />, label: 'RFM Dashboard', path: '/customers/rfm', requiredRole: 50 },
      { id: 'customers-feedback', icon: <Icon d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.7 5.225a1 1 0 00.95.69h5.494c.969 0 1.371 1.24.588 1.81l-4.448 3.232a1 1 0 00-.364 1.118l1.7 5.225c.3.921-.755 1.688-1.54 1.118l-4.449-3.232a1 1 0 00-1.175 0l-4.45 3.232c-.783.57-1.838-.197-1.54-1.118l1.7-5.225a1 1 0 00-.362-1.118L2.306 10.65c-.783-.57-.38-1.81.588-1.81h5.494a1 1 0 00.95-.69l1.7-5.225z" />, label: 'Feedback', path: '/customers/feedback', planFeature: 'marketing' },
    ],
  },
  { id: 'corporate-accounts', group: 'Customers', icon: <ModuleIcon id="corporate-accounts" />, label: 'Corporate Accounts', path: '/corporate-accounts', requiredRole: 30, hideForPlatform: true, planFeature: 'corporate_accounts' },
  {
    id: 'loyalty',
    group: 'Customers',
    icon: <ModuleIcon id="loyalty" />,
    label: 'Loyalty',
    path: '/loyalty',
    requiredRole: 50,
    hideForPlatform: true,
    planFeature: 'loyalty',
  },
  {
    id: 'vouchers',
    group: 'Customers',
    icon: <ModuleIcon id="vouchers" />,
    label: 'Vouchers',
    path: '/vouchers',
    requiredRole: 30,
    hideForPlatform: true,
    planFeature: 'loyalty',
    children: [
      { id: 'vouchers-list', icon: <Icon d="M3 10h18M3 14h18M3 18h18M3 6h18" />, label: 'All Vouchers', path: '/vouchers', planFeature: 'loyalty' },
      { id: 'vouchers-new', icon: <Icon d="M12 4v16m8-8H4" />, label: 'Create Voucher', path: '/vouchers/new', planFeature: 'loyalty' },
    ],
  },
  {
    id: 'memberships',
    group: 'Customers',
    // Crown glyph — distinct from the Loyalty star and Voucher tag.
    icon: <ModuleIcon id="memberships" />,
    label: 'Memberships',
    path: '/memberships',
    requiredRole: 50,
    hideForPlatform: true,
    planFeature: 'memberships',
    children: [
      { id: 'memberships-list', icon: <Icon d="M3 10h18M3 14h18M3 18h18M3 6h18" />, label: 'All Plans', path: '/memberships', planFeature: 'memberships' },
      { id: 'memberships-new', icon: <Icon d="M12 4v16m8-8H4" />, label: 'Create Plan', path: '/memberships/new', planFeature: 'memberships' },
    ],
  },
  {
    id: 'communications',
    group: 'Customers',
    icon: <ModuleIcon id="communications" />,
    label: 'Communications',
    path: '/communications',
    requiredRole: 30,
    hideForPlatform: true,
    planFeature: 'marketing',
    children: [
      { id: 'comm-hub', icon: <Icon d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />, label: 'Overview', path: '/communications', requiredRole: 50, planFeature: 'marketing' },
      { id: 'comm-templates', icon: <Icon d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />, label: 'Templates', path: '/communications/templates', requiredRole: 50, planFeature: 'marketing' },
      { id: 'comm-campaigns', icon: <Icon d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6" />, label: 'Campaigns', path: '/communications/campaigns', requiredRole: 50, planFeature: 'marketing' },
      { id: 'comm-deal', icon: <Icon d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />, label: 'Deal of the Day', path: '/communications/deal-of-day', requiredRole: 50, planFeature: 'marketing' },
      { id: 'comm-social', icon: <Icon d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37zm1.5-4.87h.01M6.5 3h11A3.5 3.5 0 0121 6.5v11a3.5 3.5 0 01-3.5 3.5h-11A3.5 3.5 0 013 17.5v-11A3.5 3.5 0 016.5 3z" />, label: 'Social Media', path: '/communications/social-media', requiredRole: 50, planFeature: 'marketing' },
      { id: 'comm-reorder', icon: <Icon d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm11 3h3v3h-3v-3zm-1-3h1v1h-1v-1zm3 0h3v1h-3v-1z" />, label: 'Re-order Links', path: '/communications/reorder-links', requiredRole: 30, planFeature: 'marketing' },
      { id: 'comm-logs', icon: <Icon d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />, label: 'Logs', path: '/communications/logs', planFeature: 'marketing' },
      { id: 'comm-feedback', icon: <Icon d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />, label: 'Feedback', path: '/communications/feedback', planFeature: 'marketing' },
      { id: 'comm-invoice', icon: <Icon d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />, label: 'Invoice Preview', path: '/communications/invoice-preview' },
    ],
  },
  {
    id: 'restaurant',
    group: 'Restaurant',
    icon: <ModuleIcon id="restaurant" />,
    label: 'Restaurant',
    path: '/tables',
    requiredRole: 30,
    hideForPlatform: true,
    featureFlag: 'ENABLE_RESTAURANT',
    children: [
      { id: 'tables', icon: <Icon d="M3 3h18v18H3V3zm0 6h18M9 9v12" />, label: 'Tables', path: '/tables' },
      { id: 'floor-plan', icon: <Icon d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />, label: 'Floor Plan', path: '/floor-plan' },
      { id: 'reservations', icon: <Icon d="M3 4h18v18H3V4zm13-2v4M8 2v4M3 10h18" />, label: 'Reservations', path: '/reservations' },
      { id: 'recipes', icon: <Icon d="M4 19.5A2.5 2.5 0 016.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />, label: 'Recipes', path: '/recipes' },
      { id: 'ingredients', icon: <Icon d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16zM3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />, label: 'Ingredients', path: '/ingredients' },
      { id: 'menus', icon: <Icon d="M3 12h18M3 6h18M3 18h18" />, label: 'Menus', path: '/menus' },
      { id: 'modifiers', icon: <Icon d="M4 21V14M4 10V3M12 21V12M12 8V3M20 21V16M20 12V3M1 14h6M9 8h6M17 16h6" />, label: 'Modifiers', path: '/modifiers' },
      { id: 'combos', icon: <Icon d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />, label: 'Combos', path: '/combos', requiredRole: 50 },
      // G3.15 — Kitchen staff-to-station assignment (feature-flagged)
      { id: 'kitchen-staff', icon: <Icon d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />, label: 'Station Staff', path: '/kitchen/staff-assignment', featureFlag: 'ENABLE_KDS', planFeature: 'kds' },
    ],
  },
  {
    id: 'festive',
    group: 'Restaurant',
    icon: <ModuleIcon id="festive" />,
    label: 'Bookings and Orders',
    path: '/festive-events',
    requiredRole: 30,
    hideForPlatform: true,
    featureFlag: 'ENABLE_FESTIVE_MODULE',
    planFeature: 'festive',
    children: [
      { id: 'festive-events', icon: <Icon d="M3 4h18v18H3V4zm13-2v4M8 2v4M3 10h18" />, label: 'Events', path: '/festive-events', planFeature: 'festive' },
      // G3.7: Customer Booking Management — list + calendar + detail.
      // Wired to /festivals/:id/bookings + /bookings/:id + /bookings/:id/*.
      // MANAGER+ read (matches @Roles('MANAGER') on the list + getById endpoints),
      // CASHIER+ mutate (deposit/balance/edit/cancel), MANAGER+ send reminders.
      // Platform roles hidden via the parent group's hideForPlatform.
      { id: 'bookings-list', icon: <Icon d="M16 2v4M8 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" />, label: 'Bookings', path: '/bookings', planFeature: 'festive' },
      { id: 'bookings-calendar', icon: <Icon d="M3 4h18v18H3V4zm13-2v4M8 2v4M3 10h18M7 14h2m4 0h2m-8 4h2m4 0h2" />, label: 'Booking Calendar', path: '/bookings/calendar', planFeature: 'festive' },
      { id: 'festive-orders', icon: <Icon d="M20 12v10H4V12m16 0L12 2 4 12" />, label: 'Orders', path: '/festive-orders', planFeature: 'festive' },
      { id: 'festive-alerts', icon: <Icon d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />, label: 'Alerts', path: '/festive-alerts', planFeature: 'festive' },
      { id: 'festive-capacity', icon: <Icon d="M12 20V10M18 20V4M6 20v-4" />, label: 'Capacity', path: '/festive-capacity', planFeature: 'festive' },
      { id: 'production-planning', icon: <Icon d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />, label: 'Production', path: '/production-planning', planFeature: 'demand_forecast' },
    ],
  },
  // MANAGER may view the staff list (ARCHITECTURE.md §10; GET /users is @Roles('MANAGER')).
  // Invite, edit and deactivate stay OWNER-only — the page hides them below level 50.
  { id: 'users', group: 'Staff', icon: <ModuleIcon id="users" />, label: 'Users', path: '/users', requiredRole: 30, hideForPlatform: true },
  {
    id: 'shifts',
    group: 'Staff',
    icon: <ModuleIcon id="shifts" />,
    label: 'Shifts',
    path: '/shifts',
    requiredRole: 30,
    hideForPlatform: true,
    children: [
      { id: 'shifts-list', icon: <Icon d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />, label: 'Shifts', path: '/shifts' },
      { id: 'attendance', icon: <Icon d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />, label: 'Attendance', path: '/attendance', planFeature: 'staff_attendance' },
      { id: 'shift-swaps', icon: <Icon d="M8 7h12m0 0l-4-4m4 4l-4 4m4 6H4m0 0l4 4m-4-4l4-4" />, label: 'Shift Swaps', path: '/shift-swaps', planFeature: 'staff_attendance' },
      { id: 'working-schedules', icon: <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />, label: 'Working Schedules', path: '/working-schedules', planFeature: 'staff_attendance' },
      // TODO(phase-2): cross-tenant Cash Closing view for SUPER_ADMIN/ADMIN/MODERATOR —
      // deferred per AC1/AC2/AC3. Flip `hideForPlatform` once platform-scoped
      // read endpoints land and drop-tenant leak risk has been addressed.
      { id: 'cash-closing', icon: <Icon d="M4 2h16a2 2 0 012 2v16a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2zm4 4h8m-8 4h.01m4-.01h.01m4-.01h.01m-8.01 4h.01m4-.01h.01m4-.01h.01M8 18h8" />, label: 'Cash Closing', path: '/cash-closing', requiredRole: 30, hideForPlatform: true },
      // T12: register sessions are cash closing, so — like the shift session reports — never plan-gated.
      { id: 'register-sessions', icon: <Icon d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />, label: 'Register Sessions', path: '/register-sessions', requiredRole: 30, hideForPlatform: true },
    ],
  },
  { id: 'staff-performance', group: 'Staff', icon: <ModuleIcon id="staff-performance" />, label: 'Staff Performance', path: '/staff', requiredRole: 30, hideForPlatform: true, planFeature: 'staff_attendance' },
  {
    id: 'commissions',
    group: 'Staff',
    icon: <ModuleIcon id="commissions" />,
    label: 'Commissions',
    path: '/commissions',
    // L06: both pages (Rates, Settlements) open for OWNER only.
    requiredRole: 50,
    hideForPlatform: true,
    planFeature: 'commissions',
    children: [
      { id: 'commission-rates', icon: <Icon d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />, label: 'Rates', path: '/commissions', requiredRole: 50, planFeature: 'commissions' },
      { id: 'commission-settlements', icon: <Icon d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />, label: 'Settlements', path: '/commissions/settlements', requiredRole: 50, planFeature: 'commissions' },
    ],
  },
  // Bills and Sales Invoices lead Finance (money in), followed by Expenses / Vendor Bills (money out).
  { id: 'bills', group: 'Finance', icon: <ModuleIcon id="bills" />, label: 'Bills', path: '/bills', requiredRole: 30, hideForPlatform: true },
  { id: 'sales-invoices', group: 'Finance', icon: <ModuleIcon id="sales-invoices" />, label: 'Sales Invoices', path: '/sales-invoices', requiredRole: 30, hideForPlatform: true, planFeature: 'quotations' },
  { id: 'expenses', group: 'Finance', icon: <ModuleIcon id="expenses" />, label: 'Expenses', path: '/expenses', requiredRole: 30, hideForPlatform: true },
  { id: 'vendor-bills', group: 'Finance', icon: <ModuleIcon id="vendor-bills" />, label: 'Vendor Bills', path: '/vendor-bills', requiredRole: 30, hideForPlatform: true, planFeature: 'purchase' },
  { id: 'approvals', group: 'Finance', icon: <ModuleIcon id="approvals" />, label: 'Approvals', path: '/approvals', requiredRole: 30, hideForPlatform: true },
  { id: 'exchange-rates', group: 'Finance', icon: <ModuleIcon id="exchange-rates" />, label: 'Exchange Rates', path: '/exchange-rates', requiredRole: 30, hideForPlatform: true },
  {
    id: 'reports',
    group: 'Reports',
    icon: <ModuleIcon id="reports" />,
    label: 'Reports',
    path: '/reports',
    requiredRole: 30,
    hideForPlatform: true,
    // Icons, labels and order are presentation and live here; the gating for each
    // link comes from the report registry via canAccessReport() in AdminLayout, so
    // a link is shown exactly when its route opens and its tile is offered (#253).
    // Do not add requiredRole / featureFlag here — that is what drifted before.
    children: [
      { id: 'reports-dashboard',       icon: <Icon d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />, label: 'Overview',             path: '/reports' },
      { id: 'reports-sales',           icon: <Icon d="M7 12l3-3 3 3 4-4" />, label: 'Sales',                  path: '/reports/sales' },
      { id: 'reports-gst',             icon: <Icon d="M9 7h6m-6 4h6m-6 4h6M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />, label: 'GST',                    path: '/reports/gst' },
      { id: 'reports-gstr1',           icon: <Icon d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />, label: 'GSTR1',                  path: '/reports/gstr1' },
      { id: 'reports-pnl',             icon: <Icon d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />, label: 'Profit & Loss',          path: '/reports/pnl' },
      { id: 'reports-payments',        icon: <Icon d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M9 12h12m-3-3l3 3-3 3" />, label: 'Payments',               path: '/reports/payments' },
      { id: 'reports-reconciliation',  icon: <Icon d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />, label: 'Reconciliation',         path: '/reports/reconciliation' },
      { id: 'reports-top-products',    icon: <Icon d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />, label: 'Top Products',           path: '/reports/top-products' },
      { id: 'reports-cashiers',        icon: <Icon d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />, label: 'Cashier Performance',    path: '/reports/cashiers' },
      { id: 'reports-peak-hours',      icon: <Icon d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />, label: 'Peak Hours',             path: '/reports/peak-hours' },
      { id: 'reports-discounts',       icon: <Icon d="M7 7h.01M7 3h5a2 2 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />, label: 'Discounts',              path: '/reports/discounts' },
      { id: 'reports-categories',      icon: <Icon d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2" />, label: 'Category Sales',         path: '/reports/categories' },
      { id: 'reports-top-customers',   icon: <Icon d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1z" />, label: 'Top Customers',          path: '/reports/top-customers' },
      { id: 'reports-repeat-visits',   icon: <Icon d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />, label: 'Repeat Visits',          path: '/reports/repeat-visits' },
      { id: 'reports-table-turnover',  icon: <Icon d="M4 6h16M4 10h16M4 14h16M4 18h16" />, label: 'Table Turnover',         path: '/reports/table-turnover' },
      { id: 'reports-kitchen',         icon: <Icon d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.24 17 7.25 17.73 8.855 18 10.23 18 12a8 8 0 01-.343 2.657z" />, label: 'Kitchen Performance',    path: '/reports/kitchen-performance' },
      { id: 'reports-cancellations',   icon: <Icon d="M6 18L18 6M6 6l12 12" />, label: 'Cancellation Trends',    path: '/reports/cancellation-trends' },
      { id: 'reports-expense-analysis',icon: <Icon d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />, label: 'Expense Analysis',       path: '/reports/expense-analysis' },
      { id: 'reports-staff-expense',   icon: <Icon d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />, label: 'Staff Expenses',         path: '/reports/staff-expense' },
      { id: 'reports-tally',           icon: <Icon d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />, label: 'Tally Export',           path: '/reports/tally-export' },
      { id: 'reports-purchase-register', icon: <Icon d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />, label: 'Purchase Register',      path: '/reports/purchase-register' },
      { id: 'reports-payables-ageing',   icon: <Icon d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />, label: 'Payables Ageing',        path: '/reports/payables-ageing' },
      { id: 'reports-input-tax',         icon: <Icon d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />, label: 'Input Tax',              path: '/reports/input-tax' },
      { id: 'reports-demand-forecast', icon: <Icon d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />, label: 'Demand Forecast',        path: '/reports/demand-forecast' },
    ],
  },
  // G3.9: Data Export — GDPR-compliant CSV/ZIP. OWNER+ on own tenant; platform
  // roles trigger via admin-export endpoint, not this page.
  { id: 'data-export', group: 'Reports', icon: <ModuleIcon id="data-export" />, label: 'Data Export', path: '/data-export', requiredRole: 50, hideForPlatform: true },
  { id: 'locations', group: 'Administration', icon: <ModuleIcon id="locations" />, label: 'Locations', path: '/locations', requiredRole: 50, hideForPlatform: true },
  {
    id: 'notifications',
    group: 'Administration',
    icon: <ModuleIcon id="notifications" />,
    label: 'Notifications',
    path: '/notifications',
    requiredRole: 5,
    children: [
      { id: 'notifications-inbox', icon: <Icon d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />, label: 'Inbox', path: '/notifications', requiredRole: 5 },
      { id: 'notifications-broadcasts', icon: <Icon d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />, label: 'Broadcasts', path: '/notifications/broadcasts', requiredRole: 50 },
    ],
  },
  // QA 2.5.6 — FR-AUD-007 gives MANAGER audit access limited to their own actions
  // (the API forces user_id to the caller), so the entry opens at 30, not 50.
  { id: 'audit', group: 'Administration', icon: <ModuleIcon id="audit" />, label: 'Audit Logs', path: '/audit', requiredRole: 30 },
  // G2.24: Sync Health — visible to MANAGER+ tenant users (own tenant only)
  // and SUPER_ADMIN/ADMIN/MODERATOR (cross-tenant via picker). Deliberately
  // NOT marked hideForPlatform — the platform audience is the whole point.
  { id: 'sync-health', group: 'Administration', icon: <ModuleIcon id="sync-health" />, label: 'Sync Health', path: '/sync-health', requiredRole: 30 },
  { id: 'backups', group: 'Administration', icon: <ModuleIcon id="backups" />, label: 'Backups', path: '/backups', requiredRole: 50, hideForPlatform: true },
  // G3.8: Owner-facing view of their own subscription. OWNER+ (50), hidden from platform roles.
  {
    id: 'my-subscription',
    group: 'Administration',
    icon: <ModuleIcon id="my-subscription" />,
    label: 'My Subscription',
    path: '/my-subscription',
    requiredRole: 50,
    hideForPlatform: true,
  },
  {
    id: 'settings',
    group: 'Administration',
    icon: <ModuleIcon id="settings" />,
    label: 'Settings',
    path: '/settings',
    requiredRole: 5,
    children: [
      { id: 'settings-business', icon: <Icon d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />, label: 'Business', path: '/settings', requiredRole: 50 },
      { id: 'settings-tax', icon: <Icon d="M19 5L5 19M9 7a2 2 0 11-4 0 2 2 0 014 0zm10 10a2 2 0 11-4 0 2 2 0 014 0z" />, label: 'Tax Config', path: '/settings/tax', requiredRole: 50 },
      { id: 'settings-cash-rounding', icon: <Icon d="M12 8c-2.2 0-4 .9-4 2s1.8 2 4 2 4 .9 4 2-1.8 2-4 2m0-8V6m0 10v2M4 12a8 8 0 1016 0 8 8 0 00-16 0z" />, label: 'Cash Rounding', path: '/settings/cash-rounding', requiredRole: 50 },
      { id: 'settings-notifications', icon: <Icon d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />, label: 'Notifications', path: '/settings/notifications', requiredRole: 5 },
      { id: 'settings-branding', icon: <Icon d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 011.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.555C21.965 6.012 17.461 2 12 2z" />, label: 'Branding', path: '/settings/branding', requiredRole: 50 },
      { id: 'settings-payment-gateway', icon: <Icon d="M3 10h18M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />, label: 'Payment Gateway', path: '/settings/payment-gateway', requiredRole: 50, planFeature: 'online_payments' },
      { id: 'settings-sms', icon: <Icon d="M8 10h32a2 2 0 012 2v20a2 2 0 01-2 2H20l-8 8V34H8a2 2 0 01-2-2V12a2 2 0 012-2zM14 18h20M14 24h14" />, label: 'SMS Gateway', path: '/settings/sms', requiredRole: 50 },
      { id: 'settings-email', icon: <Icon d="M6 12h36a2 2 0 012 2v20a2 2 0 01-2 2H6a2 2 0 01-2-2V14a2 2 0 012-2zM6 14l18 14L42 14" />, label: 'Email Delivery', path: '/settings/email', requiredRole: 50 },
      { id: 'settings-mfa', icon: <Icon d="M12 11c0 .55-.45 1-1 1s-1-.45-1-1 .45-1 1-1 1 .45 1 1zm5-3V7a5 5 0 00-10 0v1a2 2 0 00-2 2v9a2 2 0 002 2h10a2 2 0 002-2v-9a2 2 0 00-2-2zM9 7a3 3 0 016 0v1H9V7z" />, label: 'MFA Enforcement', path: '/settings/mfa', requiredRole: 50 },
      { id: 'settings-sidebar', icon: <Icon d="M4 6h16M4 12h10M4 18h16" />, label: 'Sidebar Modules', path: '/settings/sidebar', requiredRole: 5 },
      { id: 'settings-return-policy', icon: <Icon d="M3 10h10a4 4 0 014 4v0a4 4 0 01-4 4H7m-4-8l4-4m-4 4l4 4" />, label: 'Return Policy', path: '/settings/return-policy', requiredRole: 50 },
      // C09 (G-G1): the business's minimum margin % for store prices — a warning only.
      { id: 'settings-pricing', icon: <Icon d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />, label: 'Price Margin', path: '/settings/pricing', requiredRole: 50 },
      // QA IS4-002 (owner decision ID14): the cashier record and manager approve limits.
      { id: 'settings-wastage-limits', icon: <Icon d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />, label: 'Wastage Limits', path: '/settings/wastage-limits', requiredRole: 50 },
      { id: 'settings-inventory-tracking', icon: <Icon d="M4 7h16M4 12h16M4 17h10M7 4v3M12 4v3M17 4v3" />, label: 'Inventory Tracking', path: '/settings/inventory-tracking', requiredRole: 50, planFeature: 'batch_serial' },
    ],
  },
];

// Header title: the top-level item the path belongs to, by the same best-match
// rule as the sidebar highlight — /inventory/purchase-orders/<id> is Purchase
// Order, not Overview (/inventory), and /products/masters is Master Catalogue.
export function sidebarTitle(activePath: string): string {
  const match = matchActivePath(SIDEBAR_ITEMS.flatMap((item) => navItemPaths(item)), activePath);
  return SIDEBAR_ITEMS.find((item) => itemOwnsPath(item, match))?.label ?? '';
}

// ─── Eligibility helper ─────────────────────────────────────────────────────
// Returns the top-level sidebar items the current user is allowed to see,
// before per-user hide preferences are applied. Filters by: platform-only
// gating (hideForPlatform), feature flags, and role level. Children are
// preserved untouched. Used by both AdminLayout (sidebar rendering) and the
// Sidebar Preferences settings page (chooser list) so they stay in sync.
export function getEligibleTopLevelSidebarItems(args: {
  isPlatformUser: boolean;
  roleLevel: number;
  flags: Record<string, boolean>;
}): AdminSidebarItem[] {
  const { isPlatformUser, roleLevel, flags } = args;
  return SIDEBAR_ITEMS.filter((it) => {
    if (isPlatformUser && it.hideForPlatform) return false;
    if (it.featureFlag && !flags[it.featureFlag]) return false;
    if (it.requiredRole !== undefined && roleLevel < it.requiredRole) return false;
    return true;
  });
}

// L06: where a module tile on Home goes — the first page of the module the user
// may open. The item's own path when it has no children or one of its open
// children has that path; else the first open child's; null when no child is
// open (the tile is left out). Children are filtered by role and flag only — a
// plan-locked child still counts (lock, never hide).
export function moduleOpenPath(
  item: AdminSidebarItem,
  roleLevel: number,
  flags: Record<string, boolean>,
): string | null {
  if (!item.children) return item.path;
  const open = filterSidebarItems(item.children, roleLevel, flags);
  if (open.some((c) => c.path === item.path)) return item.path;
  return open[0]?.path ?? null;
}

// Plan-limits plan §7 — lock, never hide. Marks every item (children too) whose
// plan feature is missing as `locked`: the Sidebar and the launchpad draw a lock,
// and the route shows LockedFeature. Each item names its own feature — a child is
// not locked just because its parent is (Invoice Preview sends bills, which is
// not marketing).
export function applyPlanLocks(
  items: AdminSidebarItem[],
  hasPlanFeature: HasPlanFeature,
): AdminSidebarItem[] {
  return items.map((it) => ({
    ...it,
    locked: it.planFeature !== undefined && !hasPlanFeature(it.planFeature),
    children: it.children && applyPlanLocks(it.children, hasPlanFeature),
  }));
}

// ─── Role hierarchy ─────────────────────────────────────────────────────────

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  MODERATOR: 60,
  OWNER: 50,
  MANAGER: 30,
  CASHIER: 5,
};

// ─── Admin Layout with AppShell ─────────────────────────────────────────────

function AdminLayout({ children }: { children: React.ReactNode }) {
  const sidebarCollapsed = useSidebarPrefsStore((s) => s.collapsed);
  const toggleSidebarCollapsed = useSidebarPrefsStore((s) => s.toggleCollapsed);
  const expandedGroups = useSidebarPrefsStore((s) => s.expandedGroups);
  const setExpandedGroups = useSidebarPrefsStore((s) => s.setExpandedGroups);
  const location = useLocation();
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const logout = useAuthStore((s) => s.logout);
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const setSelectedLocationId = useDashboardStore((s) => s.setSelectedLocationId);

  const activePath = location.pathname;
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;

  // Hide per-tenant operational items from platform roles. Platform users
  // (SUPER_ADMIN/ADMIN/MODERATOR) drill into a specific tenant via /tenants/:id
  // instead of seeing these in the top-level sidebar.
  const PLATFORM_ROLES = ['SUPER_ADMIN', 'ADMIN', 'MODERATOR'];
  const isPlatformUser = PLATFORM_ROLES.includes(profile?.role ?? '');
  const isSuperAdmin = profile?.role === 'SUPER_ADMIN';
  const flags = useFeatureFlag();
  const hasPlanFeature = usePlanFeatures();
  const tax = useTaxRegime();
  const hiddenIds = useSidebarPrefsStore((s) => s.hiddenIds);
  const recordVisit = useSidebarPrefsStore((s) => s.recordVisit);
  const applyCollapsedDefault = useSidebarPrefsStore((s) => s.applyCollapsedDefault);
  useEffect(() => {
    recordVisit(activePath);
  }, [activePath, recordVisit]);
  // SUPER_ADMIN starts with the sidebar hidden and reveals it by double-clicking
  // Home. Runs once — a later manual expand is remembered.
  useEffect(() => {
    if (isSuperAdmin) applyCollapsedDefault();
  }, [isSuperAdmin, applyCollapsedDefault]);
  const newEnquiries = useNewEnquiryCount(isPlatformUser && roleLevel >= 60, activePath);
  const unreadNotifications = useUnreadNotificationCount();
  const visibleSidebarItems: SidebarItem[] = applyPlanLocks(
    getEligibleTopLevelSidebarItems({ isPlatformUser, roleLevel, flags }),
    hasPlanFeature,
  )
    // Per-user hide preferences. dashboard/settings are never hideable so
    // the user can always reach the chooser to un-hide other modules.
    .filter((it) => NON_HIDEABLE_SIDEBAR_IDS.has(it.id) || !hiddenIds.includes(it.id))
    // Gate the report links from the shared registry — the same source the tiles on
    // /reports and the route guard read. Role, vertical, feature flag and tax regime
    // are all decided by canAccessReport(), so a link can never advertise a report the
    // route then refuses (#253): Demand Forecast used to be linked whenever the tenant
    // was a restaurant, while its route also required ENABLE_FESTIVE_MODULE.
    //
    // The GST report exists for every regime and only its label follows the regime
    // vocabulary (GCC → 'VAT'); GSTR1 / Tally are India-only and drop out via the
    // registry's taxSurface gate. A report missing from the plan stays linked, with
    // a lock — the registry's planFeature, the one ReportGuard checks.
    .map((it) => {
      if (it.id !== 'reports' || !it.children) return it;
      const children = it.children
        .filter((c) => {
          const report = getReport(c.path);
          return report
            ? canAccessReport(report, {
                roleLevel,
                businessType: profile?.tenant?.business_type,
                flags,
                tax,
              })
            : true;
        })
        .map((c) => {
          const planFeature = getReport(c.path)?.planFeature;
          return planFeature && !hasPlanFeature(planFeature) ? { ...c, locked: true } : c;
        })
        .map((c) => (c.path === '/reports/gst' ? { ...c, label: tax.taxName } : c));
      return { ...it, children };
    })
    .map((it) =>
      it.id === 'platform-enquiries' && newEnquiries > 0 ? { ...it, badge: newEnquiries } : it,
    );

  // Prefer the real location_code (it is what bill numbers are prefixed with);
  // fall back to a name-derived stub only when a location has no code set.
  const locations = (profile?.locations ?? [])
    .filter((l) => l.is_active)
    .map((l) => ({
      id: l.id,
      name: l.name,
      code: l.location_code ?? l.name.substring(0, 3).toUpperCase(),
    }));
  const allLocations = headerAllLocations(roleLevel, locations.length);
  const locationIds = locations.map((l) => l.id).join(',');
  useEffect(() => {
    if (!profile) return;
    const next = headerLocationSelection(
      selectedLocationId,
      locationIds ? locationIds.split(',') : [],
      allLocations.show,
    );
    if (next !== selectedLocationId) setSelectedLocationId(next);
  }, [profile, locationIds, allLocations.show, selectedLocationId, setSelectedLocationId]);

  return (
    <AppShell
      sidebarCollapsed={sidebarCollapsed}
      onSidebarToggle={toggleSidebarCollapsed}
      // Double-click any nav icon toggles the sidebar; single click still
      // navigates. Items with a submenu are excluded (see doubleClickToToggle).
      sidebarDoubleClickToToggle
      sidebarExpandedGroups={expandedGroups}
      onSidebarExpandedGroupsChange={setExpandedGroups}
      sidebarItems={visibleSidebarItems}
      featureFlags={flags}
      activePath={activePath}
      // Sidebar navigation asks first when the open page has unsaved edits.
      onNavigate={(path) => guardedLeave(() => navigate(path))}
      user={
        profile
          ? { name: profile.name, role: profile.role, roleLevel }
          : undefined
      }
      tenant={
        profile?.tenant
          ? { name: profile.tenant.business_name }
          : undefined
      }
      notificationCount={unreadNotifications}
      onNotificationClick={() => guardedLeave(() => navigate('/notifications'))}
      title={sidebarTitle(activePath)}
      locations={locations}
      activeLocation={selectedLocationId ?? 'all'}
      onLocationChange={(id) => setSelectedLocationId(id === 'all' ? undefined : id)}
      showAllLocations={allLocations.show}
      allLocationsLabel={allLocations.label}
      userMenuItems={[
        // L05 D-E1: your own password and sessions, for every role.
        {
          id: 'account',
          label: 'My account',
          onClick: () => guardedLeave(() => navigate('/account')),
        },
        {
          id: 'logout',
          label: 'Sign Out',
          variant: 'danger',
          onClick: () => {
            guardedLeave(() => logout().then(() => navigate('/login')));
          },
        },
      ]}
    >
      <UsageBanner />
      {children}
    </AppShell>
  );
}

// ─── Report route helper ────────────────────────────────────────────────────
// Wraps a report page with ProtectedRoute + AdminLayout + ReportGuard so the
// AppRouter stays readable. Returns a <Route> element ready to drop into the
// <Routes> tree.
//
// The guard's rules are read from the report registry rather than passed in, so the
// route, the sidebar link and the tile on /reports can never gate differently (#253).
// A report route therefore must have a registry entry — an unregistered path would be
// an ungated one, so this throws rather than guessing a role level.

function renderReportRoute(path: string, element: React.ReactElement) {
  const report = getReport(path);
  if (!report) {
    throw new Error(`No report registry entry for "${path}" — add one in features/reports/registry.ts`);
  }
  return (
    <Route
      key={path}
      path={path}
      element={
        <ProtectedRoute>
          <AdminLayout>
            <ReportGuard
              minRoleLevel={report.minRoleLevel}
              vertical={report.vertical}
              featureFlag={report.featureFlag}
              planFeature={report.planFeature}
            >
              {element}
            </ReportGuard>
          </AdminLayout>
        </ProtectedRoute>
      }
    />
  );
}

// ─── Router ─────────────────────────────────────────────────────────────────

export function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <HomePage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <DashboardPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/bills"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <BillListPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/bills/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <BillDetailPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/tenants"
        element={
          <RoleProtectedRoute minRoleLevel={60}>
            <AdminLayout>
              <TenantListPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/tenants/new"
        element={
          <RoleProtectedRoute minRoleLevel={60}>
            <AdminLayout>
              <TenantCreatePage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/tenants/:id"
        element={
          <RoleProtectedRoute minRoleLevel={60}>
            <AdminLayout>
              <TenantDetailPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* G3.8: Subscription & Plan Management — platform pages */}
      <Route
        path="/subscriptions"
        element={
          <RoleProtectedRoute minRoleLevel={60}>
            <AdminLayout>
              <SubscriptionListPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/subscription-plans"
        element={
          <RoleProtectedRoute minRoleLevel={60}>
            <AdminLayout>
              <SubscriptionPlansPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* G3.8: Owner-facing view of their own tenant subscription. */}
      <Route
        path="/my-subscription"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <MySubscriptionPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <UserListPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/users/new"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <UserCreatePage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/users/:id"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <UserDetailPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/locations"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <LocationListPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations/new"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <LocationCreatePage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations/:id"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <LocationDetailPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ProductListPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/products/new"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ProductCreatePage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/products/import"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ProductImportPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/products/masters"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <MasterCataloguePage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/products/masters/conflicts"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <MasterConflictsPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/products/variant-attributes"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <VariantAttributesPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/products/:id/variants/:variantId"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ProductVariantEditPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/products/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ProductDetailPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/categories"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <CategoryListPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/categories/:id"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <CategoryDetailPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses/new"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ExpenseCreatePage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/expenses/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ExpenseDetailPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ExpenseListPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/*
        Online orders. CASHIER, matching the API's @Roles('CASHIER') on
        GET /site-orders: the person at the counter is the one who knows what
        is on the shelf, and requiring a manager would mean the owner has to
        be present for every web order. The number is 5 because this ladder is
        the admin bundle's own client-side scale (ROLE_LEVELS above), not the
        API's — a cashier is 10 there and 5 here.
      */}
      <Route
        path="/online-orders"
        element={
          <RoleProtectedRoute minRoleLevel={5}>
            <AdminLayout>
              <PlanFeatureRoute feature="online_store">
                <OnlineOrderListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/*
        The shop's own website pages. MANAGER (30), matching the API: a returns
        policy is a promise about the shop's money, so a cashier must not be
        able to rewrite it — which is the one reason this sits above the
        CASHIER level the product routes use.
      */}
      <Route
        path="/website/pages"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="online_store">
                <SiteContentPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/online-orders/:id"
        element={
          <RoleProtectedRoute minRoleLevel={5}>
            <AdminLayout>
              <PlanFeatureRoute feature="online_store">
                <OnlineOrderDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* ─── Vendor Bills (AP) — static routes MUST come before :id ─── */}
      <Route
        path="/vendor-bills/new"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <VendorBillCreatePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/vendor-bills/:id/edit"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <VendorBillCreatePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/vendor-bills/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <VendorBillDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/vendor-bills"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <VendorBillListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* ─── Sales Invoices (AR) — Finance view of invoices raised on sales orders ─── */}
      <Route
        path="/sales-invoices/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="quotations">
                <SalesInvoiceDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/sales-invoices"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="quotations">
                <SalesInvoiceListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* Sales Orders — deeper paths before :id */}
      <Route
        path="/sales-orders/:id/deliver"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="quotations">
                <SalesOrderDeliverPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/sales-orders/:id/invoice"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="quotations">
                <SalesOrderInvoicePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/sales-orders/:id"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <PlanFeatureRoute feature="quotations">
                <SalesOrderDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-orders"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <PlanFeatureRoute feature="quotations">
                <SalesOrderListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      {/* Quotations — dashboard (static) MUST be before :id (dynamic) */}
      <Route
        path="/quotations/analytics"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="quotations">
                <QuotationDashboardPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/quotations/:id"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <PlanFeatureRoute feature="quotations">
                <QuotationDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotations"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <PlanFeatureRoute feature="quotations">
                <QuotationListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/approvals"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ApprovalsPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/valuation"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <InventoryValuationPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/report"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <InventoryReportPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/low-stock"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <LowStockAlertsPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/expiry-alerts"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="batch_serial">
                <ExpiryAlertsPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <InventoryDashboardPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/wastage"
        element={
          // A cashier records wastage up to their limit (QA IS4-002, owner decision ID14).
          <RoleProtectedRoute minRoleLevel={5}>
            <AdminLayout>
              <WastageTrackingPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* ─── Stock Transfers — static routes MUST come before :id ─── */}
      <Route
        path="/inventory/stock-transfers/new"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="stock_transfers">
                <StockTransferCreatePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/stock-transfers/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="stock_transfers">
                <StockTransferDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/stock-transfers"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="stock_transfers">
                <StockTransferListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* ─── RFQs — static routes MUST come before :id ─── */}
      <Route
        path="/inventory/rfqs/new"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <RfqCreatePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/rfqs/:id/edit"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <RfqCreatePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/rfqs/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <RfqDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/rfqs"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <RfqListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* ─── Purchase Orders — static routes MUST come before :id ─── */}
      <Route
        path="/inventory/purchase-orders/new"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <PurchaseOrderCreatePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* Editing a draft is OWNER-only (minRoleLevel 50), matching @Roles('OWNER')
          on PUT /purchase-orders/:id. A manager may raise a PO, not rewrite one. */}
      <Route
        path="/inventory/purchase-orders/:id/edit"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <PurchaseOrderCreatePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* POS comments item 10: amend an approved / partly received PO. */}
      <Route
        path="/inventory/purchase-orders/:id/amend"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <PurchaseOrderAmendPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/purchase-orders/:id/receive"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <PurchaseOrderReceivePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/purchase-orders/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <PurchaseOrderDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/purchase-orders"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <PurchaseOrderListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* Purchase Order hub — Purchase PO + Sales PO tabs on one page */}
      <Route
        path="/purchase-orders"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <PurchaseOrderHubPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* Edit the DAILY PO list (owner) */}
      <Route
        path="/purchase-orders/daily"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <DailyPoTemplatePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* ─── Suppliers — static routes MUST come before :id ─── */}
      <Route
        path="/inventory/suppliers/new"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <SupplierCreatePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/suppliers/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <SupplierDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/inventory/suppliers"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="purchase">
                <SupplierListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* ─── Platform Users (G3.5) — MODERATOR+ can view, SUPER_ADMIN gates mutations inside the page.
            Static routes MUST come before :id. */}
      <Route
        path="/platform-users/new"
        element={
          <RoleProtectedRoute minRoleLevel={100}>
            <AdminLayout>
              <PlatformUserCreatePage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/platform-users/:id"
        element={
          <RoleProtectedRoute minRoleLevel={60}>
            <AdminLayout>
              <PlatformUserDetailPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/platform-users"
        element={
          <RoleProtectedRoute minRoleLevel={60}>
            <AdminLayout>
              <PlatformUserListPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* ─── Festive Module (feature-flagged: ENABLE_FESTIVE_MODULE) ───
          Static routes MUST come before dynamic :id matchers. Menu editor
          is nested under events/:id/menu. */}
      <Route
        path="/festive-events"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="festive">
                <FestiveEventsListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/festive-events/:id/menu"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="festive">
                <FestiveMenuEditorPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/festive-events/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="festive">
                <FestiveEventDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/festive-orders"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="festive">
                <FestiveOrdersPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/festive-alerts"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="festive">
                <FestiveAlertsPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/festive-capacity"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="festive">
                <FestiveCapacityPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* Lists festivals (festive) and reads the forecast (demand_forecast) — needs both. */}
      <Route
        path="/production-planning"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="festive">
                <PlanFeatureRoute feature="demand_forecast">
                  <ProductionPlanningPage />
                </PlanFeatureRoute>
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Customer Bookings (G3.7) — festival-scoped pre-orders ──────
          Static routes MUST precede the dynamic :id matcher. MANAGER+ may
          read (matches backend @Roles('MANAGER') on /festivals/:id/bookings
          and GET /bookings/:id); CASHIER+ mutations (deposit/balance/edit/
          cancel) and MANAGER+ reminders are enforced per-button in the
          detail page + by the backend. Gated behind ENABLE_FESTIVE_MODULE
          via the sidebar group + upstream API errors.                    */}
      <Route
        path="/bookings"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="festive">
                <BookingsListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/bookings/calendar"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="festive">
                <BookingsCalendarPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/bookings/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="festive">
                <BookingsDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Customers ───────────────────────────────────────────────────
          Static routes MUST come before the dynamic :id matcher, otherwise
          /customers/new, /customers/rfm and /customers/feedback would be
          captured as customer ids and 404 in the detail page.              */}
      <Route
        path="/customers"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <CustomerListPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/customers/new"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <CustomerCreatePage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/customers/rfm"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <RfmDashboardPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/customers/feedback"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="marketing">
                <CustomerFeedbackPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/customers/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <CustomerDetailPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Loyalty + Vouchers ──────────────────────────────────────────
          Static /vouchers/new MUST be declared before /vouchers/:id so the
          create page isn't swallowed as a voucher id. Same rule as the
          customers sprint. */}
      <Route
        path="/loyalty"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="loyalty">
                <LoyaltyDashboardPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/vouchers"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="loyalty">
                <VoucherListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/vouchers/new"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="loyalty">
                <VoucherCreatePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/vouchers/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="loyalty">
                <VoucherDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Memberships ─────────────────────────────────────────────────
          Static /memberships/new MUST be declared before /memberships/:id so
          the create page isn't swallowed as a plan id. Edit page is
          /memberships/:id/edit and lives after /:id per React Router's
          exact-match semantics. */}
      <Route
        path="/memberships"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="memberships">
                <MembershipPlansListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/memberships/new"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="memberships">
                <MembershipPlanCreatePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/memberships/:id"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="memberships">
                <MembershipPlanDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/memberships/:id/edit"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="memberships">
                <MembershipPlanEditPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Communications ──────────────────────────────────────────── */}
      <Route
        path="/communications"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="marketing">
                <CommunicationHubPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/communications/templates"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="marketing">
                <TemplatesPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/communications/campaigns"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="marketing">
                <CampaignsPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/communications/deal-of-day"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="marketing">
                <DealOfDayPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/communications/social-media"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="marketing">
                <SocialMediaPostCreatorPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/communications/logs"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="marketing">
                <CommunicationLogsPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/communications/feedback"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="marketing">
                <FeedbackViewerPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/communications/invoice-preview"
        element={
          <RoleProtectedRoute minRoleLevel={5}>
            <AdminLayout>
              <InvoicePreviewPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/communications/reorder-links"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="marketing">
                <ReorderLinksPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Commissions ──────────────────────────────────────────────
          Static /settlements MUST come before any :id route */}
      <Route
        path="/commissions"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="commissions">
                <CommissionRatesPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/commissions/settlements"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="commissions">
                <CommissionSettlementsPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Shifts & Attendance ─────────────────────────────────────── */}
      <Route
        path="/shifts"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ShiftManagementPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/attendance"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="staff_attendance">
                <AttendancePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/shift-swaps"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="staff_attendance">
                <ShiftSwapsPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* T15: working schedules and public holidays */}
      <Route
        path="/working-schedules"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="staff_attendance">
                <WorkingSchedulesPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Register sessions report (T12) ───────────────────────── */}
      <Route
        path="/register-sessions"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <RegisterSessionsReportPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Cash Closing ──────────────────────────────────────────── */}
      <Route
        path="/cash-closing"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <CashClosingHistoryPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Credit Accounts (feature-flagged: ENABLE_CREDIT_BILLING) ─
          Static /credit-accounts list MUST come before :id */}
      <Route
        path="/credit-accounts"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="credit_billing">
                <CreditAccountsListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/credit-accounts/:id"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="credit_billing">
                <CreditAccountDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/credit-invoices"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="credit_billing">
                <CreditInvoicesListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Corporate Accounts ────────────────────────────────────── */}
      <Route
        path="/corporate-accounts"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="corporate_accounts">
                <CorporateAccountsListPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/corporate-accounts/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="corporate_accounts">
                <CorporateAccountDetailPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Exchange Rates + Staff Performance ──────────────────────── */}
      <Route
        path="/exchange-rates"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ExchangeRatesPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/staff"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="staff_attendance">
                <StaffPerformancePage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Discount Rules + Notification Center ────────────────────── */}
      <Route
        path="/discounts"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <DiscountRulesPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <NotificationCenterPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/:id"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <NotificationDetailPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/broadcasts"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <BroadcastCenterPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Restaurant: Tables + Floor Plan ─────────────────────────── */}
      <Route
        path="/tables"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <TableManagementPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/floor-plan"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <FloorPlanEditorPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/reservations"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ReservationsPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/recipes"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <RecipeListPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/ingredients"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <IngredientListPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* G3.15 — Kitchen staff-to-station assignment (ENABLE_KDS) */}
      <Route
        path="/kitchen/staff-assignment"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <PlanFeatureRoute feature="kds">
                <StaffAssignmentPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/recipes/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <RecipeDetailPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/menus"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <MenuManagementPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/menus/periods"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <MenuPeriodsPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/modifiers"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ModifierManagementPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/combos"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <ComboListPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/combos/new"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <ComboCreatePage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/combos/:id"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <ComboDetailPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Reports ──────────────────────────────────────────────────── */}
      {renderReportRoute('/reports',                     <ReportsDashboardPage />)}
      {renderReportRoute('/reports/reconciliation',      <ReconciliationDashboardPage />)}
      {renderReportRoute('/reports/sales',               <SalesReportPage />)}
      {renderReportRoute('/reports/gst',                 <GstReportPage />)}
      {renderReportRoute('/reports/gstr1',               <Gstr1ExportPage />)}
      {renderReportRoute('/reports/pnl',                 <ProfitLossReportPage />)}
      {renderReportRoute('/reports/payments',            <PaymentBreakdownPage />)}
      {renderReportRoute('/reports/top-products',        <TopProductsReportPage />)}
      {renderReportRoute('/reports/cashiers',            <CashierPerformancePage />)}
      {renderReportRoute('/reports/peak-hours',          <PeakHoursReportPage />)}
      {renderReportRoute('/reports/discounts',           <DiscountAnalysisPage />)}
      {renderReportRoute('/reports/categories',          <CategorySalesReportPage />)}
      {renderReportRoute('/reports/top-customers',       <TopCustomersReportPage />)}
      {renderReportRoute('/reports/repeat-visits',       <RepeatVisitsReportPage />)}
      {renderReportRoute('/reports/table-turnover',      <TableTurnoverReportPage />)}
      {renderReportRoute('/reports/kitchen-performance', <KitchenPerformanceReportPage />)}
      {renderReportRoute('/reports/cancellation-trends', <CancellationTrendsReportPage />)}
      {renderReportRoute('/reports/expense-analysis',    <ExpenseAnalysisReportPage />)}
      {renderReportRoute('/reports/staff-expense',       <StaffExpenseReportPage />)}
      {renderReportRoute('/reports/tally-export',        <TallyExportPage />)}
      {renderReportRoute('/reports/purchase-register',   <PurchaseRegisterReportPage />)}
      {renderReportRoute('/reports/payables-ageing',     <PayablesAgeingReportPage />)}
      {renderReportRoute('/reports/input-tax',           <InputTaxReportPage />)}
      {renderReportRoute('/reports/demand-forecast',     <DemandForecastPage />)}

      {/* ─── Settings ──────────────────────────────────────────────── */}
      <Route
        path="/settings"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <BusinessSettingsPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/tax"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <TaxConfigPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/cash-rounding"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <CashRoundingPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <RoleProtectedRoute minRoleLevel={5}>
            <AdminLayout>
              <MyAccountPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <RoleProtectedRoute minRoleLevel={5}>
            <AdminLayout>
              <NotificationPrefsPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/branding"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <BrandingPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/payment-gateway"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="online_payments">
                <PaymentGatewayPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/sms"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <SmsConfigPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/email"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <EmailConfigPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/whatsapp"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="whatsapp">
                <WhatsAppConfigPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/mfa"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <MfaEnforcementPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/sidebar"
        element={
          <RoleProtectedRoute minRoleLevel={5}>
            <AdminLayout>
              <SidebarPreferencesPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/return-policy"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <ReturnPolicyPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/pricing"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PricingSettingsPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/wastage-limits"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <WastageLimitsPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/settings/inventory-tracking"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <PlanFeatureRoute feature="batch_serial">
                <InventoryTrackingPage />
              </PlanFeatureRoute>
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      {/* ─── Audit + Backup + Dead Letters ─────────────────────────── */}
      <Route
        path="/audit"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <AuditLogsPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/backups"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <BackupManagementPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/data-export"
        element={
          <RoleProtectedRoute minRoleLevel={50}>
            <AdminLayout>
              <DataExportPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/dead-letters"
        element={
          <RoleProtectedRoute minRoleLevel={80}>
            <AdminLayout>
              <DeadLettersPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* G2.24: Sync Health — MANAGER+ for own tenant; MODERATOR+ for tenant
          override via the in-page picker. Backend enforces the override
          gate in resolveEffectiveTenant(). */}
      <Route
        path="/sync-health"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <AdminSyncHealthPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* T09: offline bills whose till tax rate differed from the product
          master — MANAGER+ for their own tenant (own locations for a manager). */}
      <Route
        path="/sync-health/tax-mismatches"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <TaxMismatchesPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* T19: offline card/UPI payments a MANAGER+ verifies (reconciliation).
          Not plan-gated — offline billing is the till path on every plan. */}
      <Route
        path="/sync-health/offline-payments"
        element={
          <RoleProtectedRoute minRoleLevel={30}>
            <AdminLayout>
              <OfflinePaymentsPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* QA-ADM-455: System Health — DB / Redis / RabbitMQ / S3 probe status.
          Platform-only (MODERATOR+ = 60); tenant users have no reason to see
          raw infrastructure status. */}
      <Route
        path="/system-health"
        element={
          <RoleProtectedRoute minRoleLevel={60}>
            <AdminLayout>
              <SystemHealthPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* Email SMTP plan §7: the platform's own sending account. ADMIN (80) reads,
          SUPER_ADMIN writes — the page decides that, the controller enforces it. */}
      <Route
        path="/platform/email"
        element={
          <RoleProtectedRoute minRoleLevel={80}>
            <AdminLayout>
              <PlatformEmailPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* Website plan §6.2: the company facts the website reads. ADMIN (80) reads,
          SUPER_ADMIN writes — the page decides that, the controller enforces it. */}
      <Route
        path="/platform/company-profile"
        element={
          <RoleProtectedRoute minRoleLevel={80}>
            <AdminLayout>
              <CompanyProfilePage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />
      {/* Website plan §6.4: website enquiries. MODERATOR (60) and above. */}
      <Route
        path="/platform/enquiries"
        element={
          <RoleProtectedRoute minRoleLevel={60}>
            <AdminLayout>
              <EnquiriesPage />
            </AdminLayout>
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <div className="p-6 text-neutral-500">Page coming soon...</div>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
